import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastType } from '@elliemae/ds-toast';
import ssf from '@elliemae/em-ssf-guest';
import { PATH_SCENARIO_SEARCH } from '../../../route/constants';
import {
  ChannelOption,
  KNOWN_CHANNEL_LABELS,
  CHANNEL_TO_LOAN_CHANNEL,
  WHOLESALE_CHANNEL_TYPES,
} from '../components/ScenarioHeader/constants';
import { AppDispatch } from '../../../store';
import { getZeroBasedPricingSelector } from '../../../data/lockSummary/actionCreators';

interface UseChannelTypesResult {
  channels: ChannelOption[];
  isLoading: boolean;
  refetch: () => void;
}

export function useChannelTypes(
  selectedChannel: ChannelOption | null,
  setSelectedChannel: (channel: ChannelOption) => void,
  dispatch: AppDispatch,
  setIsSingleChannel?: (value: boolean) => void,
  lookupsReady = false,
): UseChannelTypesResult {
  const navigate = useNavigate();
  const [channels, setChannels] = useState<ChannelOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetched = useRef(false);
  const pendingSingleChannel = useRef<ChannelOption | null>(null);

  const fetchChannelTypes = useCallback(async () => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    try {
      setIsLoading(true);
      const transactionObj = await ssf.getObject('transaction');
      const originData = await transactionObj.getOrigin();

      console.log(
        '[useChannelTypes] SSF channelTypes:',
        originData?.settings?.TPOAdminSettings?.channelTypes,
      );

      const availableChannels: ChannelOption[] = [];

      if (originData?.settings?.TPOAdminSettings?.channelTypes?.length > 0) {
        originData?.settings?.TPOAdminSettings?.channelTypes.forEach(
          (channelId) => {
            if (channelId === 'Correspondent') {
              originData?.settings?.TPOAdminSettings?.correspondentSelection.forEach(
                (correspondentChannelId: 'Delegated' | 'NonDelegated') => {
                  availableChannels.push({
                    id: correspondentChannelId,
                    label: KNOWN_CHANNEL_LABELS[correspondentChannelId],
                    wsChannel: WHOLESALE_CHANNEL_TYPES[correspondentChannelId],
                    value: CHANNEL_TO_LOAN_CHANNEL[correspondentChannelId],
                  });
                },
              );
            } else {
              availableChannels.push({
                id: channelId,
                label: KNOWN_CHANNEL_LABELS[channelId],
                value: CHANNEL_TO_LOAN_CHANNEL[channelId],
              });
            }
          },
        );
      } else {
        toast(
          {
            type: ToastType.ERROR,
            messageTitle: 'No Channels Available',
            messageText: 'No supported channel types were returned from TPOC',
          },
          {},
        );
      }

      if (availableChannels.length === 1) {
        setIsSingleChannel?.(true);
        setSelectedChannel(availableChannels[0]);
        pendingSingleChannel.current = availableChannels[0];
        return;
      }

      setChannels(availableChannels);

      if (!selectedChannel) {
        setSelectedChannel(availableChannels[0]);
      }
    } catch (err) {
      console.error('Error fetching channel types:', err);
    } finally {
      setIsLoading(false);
    }
  }, [navigate, selectedChannel, setSelectedChannel, dispatch]);

  useEffect(() => {
    fetchChannelTypes();
  }, [fetchChannelTypes]);

  // Single-channel: wait for lookups before navigating to Search
  useEffect(() => {
    if (!pendingSingleChannel.current || !lookupsReady) return;
    const channel = pendingSingleChannel.current;
    pendingSingleChannel.current = null;
    dispatch(getZeroBasedPricingSelector(channel.id) as any).then(() => {
      navigate(PATH_SCENARIO_SEARCH, { replace: true });
    });
  }, [lookupsReady, dispatch, navigate]);

  return {
    channels,
    isLoading,
    refetch: fetchChannelTypes,
  };
}
